import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ReactNode } from "react";

import {
  ChannelsContext,
  ChannelsContextType,
  UpdateChannelCurrentMonthPayload,
  UpdateChannelMonthlyPayload,
} from "./ChannelsContext";

import { campaignJobBoardQueryKeys } from "@/features/campaigns/jobboards/api/queryKeys";
import { clientQueryKeys } from "@/features/clients/api/query-keys";
import { invoiceQueryKeys } from "@/features/invoices/api/queryKeys";
import { instance } from "@/lib/axios/instance";
import { ChannelType } from "@/types/response/ChannelType";
import { PagedResponse } from "@/types/response/PagedResponse";
import { ValidationErrorResponse } from "@/types/response/ValidationResponse";

type ChannelProviderProps = {
  channelId?: string;
  children: ReactNode;
  clientId?: string;
  descending?: boolean;
  isInvoiced?: boolean;
  month?: string;
  orderBy?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  year?: string;
};

export const ChannelProvider = ({
  channelId,
  children,
  clientId,
  isInvoiced = false,
  month,
  page = 1,
  pageSize = 10,
  search,
  year,
}: ChannelProviderProps) => {
  const queryClient = useQueryClient();

  const {
    data: clientChannels,
    isLoading: clientChannelsLoading,
    isFetching: clientChannelsFetching,
  }: UseQueryResult<PagedResponse<ChannelType>, AxiosError> = useQuery({
    queryKey: ["clientChannels", clientId, month, year, page, pageSize, search],
    queryFn: () =>
      instance
        .get<PagedResponse<ChannelType>>(
          `/api/v2/Clients/${clientId}/Channels/${year}/${month}`,
          {
            params: {
              isInvoiced,
              page,
              pageSize,
              search,
            },
          }
        )
        .then((res) => res.data),
    refetchOnMount: true,
    refetchOnReconnect: false,
    staleTime: 20 * 60 * 1000,
    enabled: !!clientId,
    placeholderData: keepPreviousData,
  });

  const { data: channel } = useQuery({
    queryKey: ["channel", channelId],
    queryFn: () =>
      instance
        .get<ChannelType>(`/api/v2/Channels/${channelId}`)
        .then((res) => res.data),
    refetchOnReconnect: false,
    staleTime: 20 * 60 * 1000,
    enabled: !!channelId,
  });

  const updateCurrentMonthBudget = useMutation<
    void,
    AxiosError<ValidationErrorResponse>,
    UpdateChannelCurrentMonthPayload,
    { previousChannel?: ChannelType; previousList?: PagedResponse<ChannelType> }
  >({
    mutationFn: async (
      variables: UpdateChannelCurrentMonthPayload
    ): Promise<void> => {
      const { channelId, payload } = variables;
      await instance.put<void>(
        `/api/v2/Clients/${clientId}/Channels/${channelId}/Budget/Current`,
        payload
      );
    },
    onMutate: async ({ channelId: mutChannelId, payload }) => {
      const channelKey = ["channel", mutChannelId];
      const listKey = [
        "clientChannels",
        clientId,
        month,
        year,
        page,
        pageSize,
        search,
      ];

      await Promise.all([
        queryClient.cancelQueries({ queryKey: channelKey }),
        queryClient.cancelQueries({
          queryKey: invoiceQueryKeys.cache.clientChannels.byClient(clientId),
        }),
      ]);

      const previousChannel =
        queryClient.getQueryData<ChannelType>(channelKey);
      const previousList =
        queryClient.getQueryData<PagedResponse<ChannelType>>(listKey);

      if (previousChannel?.selectedMonth) {
        queryClient.setQueryData<ChannelType>(channelKey, {
          ...previousChannel,
          selectedMonth: {
            ...previousChannel.selectedMonth,
            budget: payload.budget,
          },
        });
      }

      if (previousList) {
        queryClient.setQueryData<PagedResponse<ChannelType>>(listKey, {
          ...previousList,
          data: previousList.data.map((ch) =>
            ch.id === mutChannelId && ch.selectedMonth
              ? {
                ...ch,
                selectedMonth: {
                  ...ch.selectedMonth,
                  budget: payload.budget,
                },
              }
              : ch
          ),
        });
      }

      return { previousChannel, previousList };
    },
    onError: (_, { channelId: mutChannelId }, context) => {
      if (context?.previousChannel) {
        queryClient.setQueryData(
          ["channel", mutChannelId],
          context.previousChannel
        );
      }
      if (context?.previousList) {
        queryClient.setQueryData(
          [
            "clientChannels",
            clientId,
            month,
            year,
            page,
            pageSize,
            search,
          ],
          context.previousList
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.cache.channels.all(),
      });
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.cache.clientChannels.all(),
      });
      if (clientId) {
        queryClient.invalidateQueries({
          queryKey: invoiceQueryKeys.cache.transactions.byClient(clientId),
        });
        queryClient.invalidateQueries({
          queryKey: clientQueryKeys.detail(clientId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: campaignJobBoardQueryKeys.clientBudgetMonth.all,
      });
    },
  });

  const updateMonthlyBudget = useMutation<
    void,
    AxiosError<ValidationErrorResponse>,
    UpdateChannelMonthlyPayload,
    { previousChannel?: ChannelType; previousList?: PagedResponse<ChannelType> }
  >({
    mutationFn: async (
      variables: UpdateChannelMonthlyPayload
    ): Promise<void> => {
      const { channelId, payload } = variables;
      await instance.put<void>(
        `/api/v2/Clients/${clientId}/Channels/${channelId}/Budget/Monthly`,
        payload
      );
    },
    onMutate: async ({ channelId: mutChannelId, payload }) => {
      const channelKey = ["channel", mutChannelId];
      const listKey = [
        "clientChannels",
        clientId,
        month,
        year,
        page,
        pageSize,
        search,
      ];

      await Promise.all([
        queryClient.cancelQueries({ queryKey: channelKey }),
        queryClient.cancelQueries({
          queryKey: invoiceQueryKeys.cache.clientChannels.byClient(clientId),
        }),
      ]);

      const previousChannel =
        queryClient.getQueryData<ChannelType>(channelKey);
      const previousList =
        queryClient.getQueryData<PagedResponse<ChannelType>>(listKey);

      if (previousChannel?.selectedMonth) {
        queryClient.setQueryData<ChannelType>(channelKey, {
          ...previousChannel,
          selectedMonth: {
            ...previousChannel.selectedMonth,
            budget: payload.budget,
          },
        });
      }

      if (previousList) {
        queryClient.setQueryData<PagedResponse<ChannelType>>(listKey, {
          ...previousList,
          data: previousList.data.map((ch) =>
            ch.id === mutChannelId && ch.selectedMonth
              ? {
                ...ch,
                selectedMonth: {
                  ...ch.selectedMonth,
                  budget: payload.budget,
                },
              }
              : ch
          ),
        });
      }

      return { previousChannel, previousList };
    },
    onError: (_, { channelId: mutChannelId }, context) => {
      if (context?.previousChannel) {
        queryClient.setQueryData(
          ["channel", mutChannelId],
          context.previousChannel
        );
      }
      if (context?.previousList) {
        queryClient.setQueryData(
          [
            "clientChannels",
            clientId,
            month,
            year,
            page,
            pageSize,
            search,
          ],
          context.previousList
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.cache.channels.all(),
      });
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.cache.clientChannels.all(),
      });
      if (clientId) {
        queryClient.invalidateQueries({
          queryKey: invoiceQueryKeys.cache.transactions.byClient(clientId),
        });
        queryClient.invalidateQueries({
          queryKey: clientQueryKeys.detail(clientId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: campaignJobBoardQueryKeys.clientBudgetMonth.all,
      });
    },
  });

  const contextValue: ChannelsContextType = {
    channel,
    updateCurrentMonthBudget,
    updateMonthlyBudget,
    clientChannels,
    clientChannelsLoading,
    clientChannelsFetching,
  };

  return (
    <ChannelsContext.Provider value={contextValue}>
      {children}
    </ChannelsContext.Provider>
  );
};
